// externals
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// libraries
import { Box } from '@interstate/components/Box';
import { dateUtils } from '@makemydeal/dr-common-utils';
import * as dealHistoryActionCreators from '@makemydeal/dr-dash-store/actions/dealHistoryActionCreator';
import * as dealHistorySelectors from '@makemydeal/dr-dash-store/selectors/dealHistorySelectors';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as quickCompareSelectors from '@makemydeal/dr-dash-store/selectors/quickCompareSelectors';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { StateTree } from '@makemydeal/dr-dash-types';
import type { IPencilOfferInputs, IShopper, Offer, OfferType, PaymentTerm } from '@makemydeal/dr-platform-types';
import { CASH, FINANCE, LEASE, BALLOON } from '@makemydeal/dr-platform-types';

// interfaces/types
import type { IOfferCompareColumnProps } from './types/offerCompareColumnProps';

// components
import { MakeCurrentModal } from '../makeCurrentModal/MakeCurrentModal.interstate';
import { MakeCurrentButtonView } from '../makeCurrentModal/types';
import { OfferCardContent } from '../offerCardContent/OfferCardContent';
import OfferCardHeader from '../offerCardHeader/OfferCardHeader.interstate';
import { OfferCardStock } from '../offerCardStock/OfferCardStock';
import CompareColumnSummary from './CompareColumnSummary.interstate';
import { CompareCashColumnDetail } from './compareColumnDetails/CompareCashColumnDetail.interstate';
import {
    OfferCompareCardBlock,
    OfferCompareColumnsFooterWrapper,
    OfferCompareColumnsHeaderWrapper
} from './compareColumnDetails/CompareColumnDetail.style';
import { CompareFinanceColumnDetail } from './compareColumnDetails/CompareFinanceColumnDetail.interstate';
import { CompareLeaseColumnDetail } from './compareColumnDetails/CompareLeaseColumnDetail.interstate';

// utils
import { OfferCardBadges } from '../offerCardBadges/OfferCardBadges';
import { determineDeltas } from '../utils/determineDeltasUtil';

const OfferCompareColumn = (props: IOfferCompareColumnProps) => {
    const {
        isCurrent,
        isDealHistory,
        isQuickCompare,
        onMakeCurrentOffer,
        isDefaultOffer,
        isMostRecentDealerOffer,
        isMostRecentShopperOffer,
        deal,
        isCompareScenario,
        scenarioCount = 0,
        isPrimary
    } = props;

    const dispatch = useDispatch();
    const workingOffer = useSelector(offerSelectors.getWorkingOffer);
    const comparingOffer = useSelector(quickCompareSelectors.getQuickCompareOffer);
    const selectedOffers = useSelector(dealHistorySelectors.getSelectedDealHistory);
    const isDealScopedNetTradeEnabled = useSelector(featureToggleSelectors.isDealScopedNetTradeEnabled);
    const deltaList = isCompareScenario
        ? determineDeltas(undefined, undefined)
        : determineDeltas(deal, workingOffer, isCurrent, isDealScopedNetTradeEnabled);

    const { shopper, createdOn, offerType, terms, penciledInputs, offerSource, applicationName, vehicle } = deal.offer;
    const { firstName, lastName } = shopper || ({} as IShopper);

    const offerPrice = offerSelectors.getSellingPrice(deal as unknown as StateTree);

    const payment = offerSelectors.getPaymentTerm(offerType as OfferType, penciledInputs, terms);

    const offerTypesDiffer = (offersList: Offer[]) => {
        return offersList.some((currOffer) => currOffer.offerType !== workingOffer.offer.offerType);
    };

    const hasDifference = () => {
        if (isCompareScenario) return false;
        if (isDealHistory) {
            return offerTypesDiffer([...selectedOffers.map((deal) => deal.offer)]);
        } else {
            return offerTypesDiffer([comparingOffer.offer]);
        }
    };

    const renderColumnDetail = () => {
        const _hasDifference = hasDifference();
        switch (offerType as OfferType) {
            case CASH:
                return <CompareCashColumnDetail deal={deal} hasDifference={_hasDifference} deltaList={deltaList} />;
            case FINANCE:
                return <CompareFinanceColumnDetail deal={deal} hasDifference={_hasDifference} deltaList={deltaList} />;
            case LEASE:
                return <CompareLeaseColumnDetail deal={deal} hasDifference={_hasDifference} deltaList={deltaList} />;
            case BALLOON:
                // TODO: Create dedicated CompareBalloonColumnDetail component for balloon-specific rendering
                return <CompareFinanceColumnDetail deal={deal} hasDifference={_hasDifference} deltaList={deltaList} />;
        }
    };

    const createdOnFormattedDate = createdOn && dateUtils.formatAsMMDDYYYY(new Date(createdOn));
    const createdOnFormattedTime = createdOn && dateUtils.formatAsTime(new Date(createdOn));

    const onHistoryScenariosClick = useCallback(() => {
        dispatch(dealHistoryActionCreators.setDealHistoryScenariosDrawerDeal(deal));
    }, [dispatch, deal]);

    return (
        <>
            <OfferCompareColumnsHeaderWrapper>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    data-testid="offer-compare-column-offer-card-header"
                >
                    <OfferCardHeader
                        createdOnDate={createdOnFormattedDate}
                        createdOnTime={createdOnFormattedTime}
                        offerSource={offerSource}
                        applicationName={applicationName}
                        isDefaultOffer={isDefaultOffer}
                        isMostRecentDealerOffer={isMostRecentDealerOffer}
                        isMostRecentShopperOffer={isMostRecentShopperOffer}
                        showSubHeader={false}
                    />
                    {!isQuickCompare && (
                        <OfferCardBadges
                            isCurrent={isCurrent}
                            isPrimary={isPrimary}
                            scenarioCount={scenarioCount}
                            toggleModal={onHistoryScenariosClick}
                        />
                    )}
                </Box>
                <CompareColumnSummary
                    isCurrentOffer={isCurrent}
                    createdOn={createdOn as string}
                    offerType={offerType as OfferType}
                    lastName={lastName}
                    firstName={firstName}
                    offerSource={offerSource}
                    offerPrice={offerPrice}
                    payment={payment as PaymentTerm | IPencilOfferInputs}
                    isDealHistory={isDealHistory}
                    isDefaultOffer={isDefaultOffer}
                    isMostRecentDealerOffer={isMostRecentDealerOffer}
                    isMostRecentShopperOffer={isMostRecentShopperOffer}
                />

                <Box display="grid" gap={1} marginTop={2} marginBottom={2} data-testid="offer-compare-column-offer-card-content">
                    <Box
                        {...(isQuickCompare && {
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        })}
                    >
                        <OfferCardStock stockNumber={vehicle?.stockNumber} />
                        {isQuickCompare && (
                            <OfferCardBadges
                                isCurrent={isCurrent}
                                isPrimary={isPrimary}
                                scenarioCount={scenarioCount}
                                toggleModal={onHistoryScenariosClick}
                            />
                        )}
                    </Box>
                    <OfferCardContent offer={deal.offer} />
                </Box>
            </OfferCompareColumnsHeaderWrapper>
            {offerType && renderColumnDetail()}
            <OfferCompareColumnsFooterWrapper>
                {!isCurrent && onMakeCurrentOffer && (
                    <OfferCompareCardBlock data-testid="make-current" rowName="make-current">
                        <MakeCurrentModal
                            onMakeCurrentOffer={onMakeCurrentOffer}
                            makeCurrentButtonView={
                                isDealHistory ? MakeCurrentButtonView.DealHistory : MakeCurrentButtonView.QuickCompare
                            }
                        />
                    </OfferCompareCardBlock>
                )}
            </OfferCompareColumnsFooterWrapper>
        </>
    );
};

export default OfferCompareColumn;
